import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import {
  Block,
  Chip,
  CoachButton,
  ErrorNote,
  Eyebrow,
  inputClass,
  Loading,
  Screen,
} from '../components/primitives';
import { coaching, fmtPeso } from '../lib/api';
import { longDay } from '../lib/format';
import { qk } from '../lib/queries';

const RANGES = [7, 30, 365] as const;
const RANGE_LABELS: Record<number, string> = { 7: 'This week', 30: 'This month', 365: 'All' };

/**
 * The member's own log of what showed up.
 *
 * There is no total on this screen, of the member's own rows or of anyone
 * else's. A list of your own notes is a journal; a running "manifested" figure
 * is an income claim, and this product does not make one. The amount is
 * optional and only ever appears beside the win it belongs to.
 */
export function MiracleWinsPage() {
  const qc = useQueryClient();
  const [days, setDays] = useState<number>(30);
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const list = useQuery({ queryKey: qk.wins(days), queryFn: () => coaching.wins({ days }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['coaching', 'wins'] });

  const add = useMutation({
    mutationFn: () => coaching.createWin({ note, amount: amount || null }),
    meta: { successMessage: 'Logged.' },
    onSuccess: () => {
      setNote('');
      setAmount('');
      void invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => coaching.deleteWin(id),
    meta: { successMessage: 'Removed.' },
    onSuccess: () => void invalidate(),
  });

  const canSave = note.trim().length >= 2 && !add.isPending;

  return (
    <Screen
      back="/manifest"
      eyebrow="Miracle wins"
      title="What showed up"
      subtitle="Write down the good things as they arrive, however small. Noticing them is the practice."
    >
      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-3">Log a win</Eyebrow>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Something good that showed up today…"
          className={`${inputClass} resize-none`}
        />
        <label className="mt-4 block">
          <span className="eyebrow">What it was worth (optional)</span>
          <input
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
            inputMode="decimal"
            placeholder="0.00"
            className={`${inputClass} mt-2`}
          />
        </label>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          Only you ever see this. It is not added up and it is not shown to anyone else.
        </p>
        <CoachButton
          kind="gold"
          className="mt-5"
          disabled={!canSave}
          onClick={() => canSave && add.mutate()}
        >
          {add.isPending ? 'Saving…' : 'Log it'}
        </CoachButton>
      </section>

      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-3">Show</Eyebrow>
        <div className="flex flex-wrap gap-2">
          {RANGES.map((r) => (
            <Chip key={r} selected={days === r} onClick={() => setDays(r)}>
              {RANGE_LABELS[r]}
            </Chip>
          ))}
        </div>
      </section>

      {list.isPending && <Loading />}
      {list.isError && <ErrorNote error={list.error} retry={() => void list.refetch()} />}
      {list.data &&
        (list.data.wins.length === 0 ? (
          <Block>
            Nothing logged yet in this stretch. The next good thing that turns up, however ordinary,
            is worth a line here.
          </Block>
        ) : (
          <ul>
            {list.data.wins.map((win) => (
              <li
                key={win.id}
                className="flex items-start gap-3.5 border-b border-border px-5 py-4"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[0.95rem] leading-relaxed text-ink">{win.note}</p>
                  <p className="eyebrow mt-1.5">
                    {longDay(win.day)}
                    {win.amount && ` · ${fmtPeso(win.amount)}`}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label={`Remove the win logged on ${longDay(win.day)}`}
                  className="mt-0.5 shrink-0 p-1 text-sage hover:text-ink"
                  onClick={() => remove.mutate(win.id)}
                  disabled={remove.isPending}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        ))}
    </Screen>
  );
}
